import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  CasesIdentityVerificationV1DraftContainerApplicantUserPaymentInstrumentDocument,
  CasesIdentityVerificationV1DraftContainerIdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsFieldsFragment,
  CasesIdentityVerificationV1DraftContainerIdentityVerificationCaseV1DeleteDocument,
  CasesIdentityVerificationV1DraftContainerIdentityVerificationCaseV1Document,
  CasesIdentityVerificationV1DraftContainerIdentityVerificationCaseV1UpdateDocument,
  CasesIdentityVerificationV1DraftContainerProcessPaymentPollingDocument,
  FileInfo,
  IdentityVerificationCaseV1CaseDetailsApplicationInput
} from '../../../../../../../../generated';
import { lookupCountryQuery } from './case-details-country-query';
import { Country } from '../../../../../../../../types/country';
import { ComponentQueryLoader } from '../../../../../../../shared/component-query-loader';
import { AzureBlobResponse } from '../../../../../../../shared/file-upload/azure-upload';
import { Draft } from './draft';
import { useEffect, useState } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';

interface DraftDetailContainerProps {
  caseId: string;
  applicantUserId: string;
}

export const DraftContainer: React.FC<DraftDetailContainerProps> = (props) => {
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { data: countryData, loading: countryLoading } = useQuery(lookupCountryQuery);
  
  const {
    data: paymentInstrumentData,
    loading: paymentInstrumentLoading,
    error: paymentInstrumentError
  } = useQuery(CasesIdentityVerificationV1DraftContainerApplicantUserPaymentInstrumentDocument, {
    variables: {
      applicantUserId: props.applicantUserId
    }
  });

const [deleteCase] = useMutation(CasesIdentityVerificationV1DraftContainerIdentityVerificationCaseV1DeleteDocument, {
  // necessary to remove case from active cases list after withdrawal
  update: (cache, { data }) => {
    if (data?.identityVerificationCaseV1Delete?.status?.success === true) {
      cache.evict({ id: `IdentityVerificationCaseV1:${props.caseId}` });
    }
  }
});

const {
    data: caseData,
    loading: caseLoading,
    error: caseError,
    refetch: getIdentityVerificationCaseV1
  } = useQuery(CasesIdentityVerificationV1DraftContainerIdentityVerificationCaseV1Document, {
    variables: {
      identityVerificationCaseV1Id: props.caseId
    }
  });

  const [updateCase] = useMutation(CasesIdentityVerificationV1DraftContainerIdentityVerificationCaseV1UpdateDocument);
  const [_processPaymentPolling, { data: processPaymentPollingData, startPolling: startProcessPaymentPolling, stopPolling: stopProcessPaymentPolling }] = useLazyQuery(
    CasesIdentityVerificationV1DraftContainerProcessPaymentPollingDocument,
    {
      variables: {
        identityVerificationCaseV1Id: props.caseId
      }
    }
  );

  const fetchFilesData = async () => {
    try {
      const { data } = await getIdentityVerificationCaseV1({
        identityVerificationCaseV1Id: props.caseId
      });
      return data?.identityVerificationCaseV1?.files as FileInfo[];
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    // to update state
    const doRefetchCaseData = async () => {
      await getIdentityVerificationCaseV1({
        identityVerificationCaseV1Id: props.caseId
      });
      navigate(`../active/v1`);
    };

    if (processPaymentPollingData?.identityVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.isSuccess === true) {
      stopProcessPaymentPolling();
      message.success('Application submitted successfully');
      doRefetchCaseData();
    } else if (processPaymentPollingData?.identityVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.isSuccess === false) {
      stopProcessPaymentPolling();
      setFormLoading(false);
      message.error('Payment failed: ' + processPaymentPollingData?.identityVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.errorMessage);
    }
  }, [processPaymentPollingData]);

  const handleSubmit = async (values: IdentityVerificationCaseV1CaseDetailsApplicationInput) => {
    setFormLoading(true);
    try {
      const result = await updateCase({
        variables: {
          input: {
            id: props.caseId,
            caseDetails: {
              application: {
                passportDetails: values.passportDetails,
                isAttestationComplete: values.isAttestationComplete
              }
            },
            payment: {
              paymentInstrumentId: paymentInstrumentData?.applicantUserPaymentInstrument?.paymentInstrumentId,
              paymentAmount: caseData?.identityVerificationCaseV1?.financeDetails?.serviceFee as number
            }
          }
        }
      });

      if (
        result.data?.identityVerificationCaseV1Update.status.success &&
        result.data.identityVerificationCaseV1Update.identityVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.status === 'PENDING'
      ) {
        try {
          startProcessPaymentPolling(5000);
        } catch (error: any) {
          message.error(error.message);
          setFormLoading(false);
        }
      } else {
        message.error('Application submission failed');
        setFormLoading(false);
      }
    } catch (error) {
      message.error('Application submission failed');
      setFormLoading(false);
    }
  };


  const handleWithdrawRequest = async () => {
    setFormLoading(true);
    const { data } = await deleteCase({
      variables: {
        input: {
          caseId: props.caseId,
        },
      },
    });
    if (data?.identityVerificationCaseV1Delete?.status?.success === true) {
      navigate("/applicant/cases");
      message.success("Case withdrawn successfully");
    } else {
      message.error("Failed to withdraw case");
    }
    setFormLoading(false);
  }

  const handleOnUploadSuccess = async (uploadedData: AzureBlobResponse, fieldName: string) => {
    const assetName = fieldName === 'photo' ? 'photoAssets' : 'passportAssets';
    const { blobUrl, versionId } = uploadedData;
    const input = {
      id: props.caseId,
      caseDetails: {
        application: {
          [assetName]: {
            [`${fieldName}`]: blobUrl,
            [`${fieldName}Version`]: versionId
          }
        }
      }
    };
    updateCase({
      variables: {
        input
      }
    }).then((response) => {
      if (response.data?.identityVerificationCaseV1Update.status.success) {
        message.success('File uploaded successfully');
      } else {
        message.error('Error saving Application data');
      }
    });
  };

  return (
    <ComponentQueryLoader
      error={caseError || paymentInstrumentError}
      errorComponent={
        <div>
          {caseError && <div>{caseError.message}</div>}
          {paymentInstrumentError && <div>{paymentInstrumentError.message}</div>}
        </div>
      }
      loading={countryLoading || caseLoading || paymentInstrumentLoading}
      hasData={countryData?.countries?.eventPayload?.countries && caseData?.identityVerificationCaseV1?.caseDetails}
      hasDataComponent={
        <Draft
          applicationPassportDetails={
            caseData?.identityVerificationCaseV1?.caseDetails?.application
              ?.passportDetails as CasesIdentityVerificationV1DraftContainerIdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsFieldsFragment
          }
          isAttestationComplete={caseData?.identityVerificationCaseV1?.caseDetails?.application?.isAttestationComplete as boolean}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          onSubmit={handleSubmit}
          caseId={props.caseId ?? ''}
          fetchFilesData={fetchFilesData}
          onUploadSuccess={handleOnUploadSuccess}
          onWithdrawRequest={handleWithdrawRequest}
          formLoading={formLoading}
          serviceFee={caseData?.identityVerificationCaseV1?.financeDetails?.serviceFee as number}
        />
      }
    />
  );
};

